import React, { useEffect, useMemo, useRef } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';

import useScreen from '@lyearn/core/hooks/useScreen';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import ErrorState, { ErrorBoundary } from '@/components/Error';
import useSearchParam from '@/hooks/useSearchParam';

import { HomeEditorQueryKey } from '../../../../../Editor/types';
import { DEFAULT_LAYOUT_GENERIC, getLayoutSpan } from '../../../../utils/layouts';
import { heightRestriction } from './styles';
import { WidgetContainerProps } from './types';

const WidgetContainer: React.FC<React.PropsWithChildren<WidgetContainerProps>> = (props) => {
  const { className, pageComponentId, layout, enforceHeightRestriction, children, isMobile } =
    props;
  const ref = useRef<HTMLDivElement | null>(null);
  const { isDesktop } = useScreen();
  const prevIsDesktopRef = useRef(isDesktop);

  const currentWidgetIndex = useSearchParam(HomeEditorQueryKey.PageComponent);
  const navigationView = useSearchParam(HomeEditorQueryKey.Navigation);
  useEffect(() => {
    const hasPreviewLayoutChanged = prevIsDesktopRef.current !== isDesktop;
    prevIsDesktopRef.current = isDesktop;
    const delay = hasPreviewLayoutChanged ? 500 : 0;

    setTimeout(() => {
      if (ref.current && currentWidgetIndex === pageComponentId && !navigationView?.length) {
        scrollIntoView(ref.current, {
          behavior: 'smooth',
          block: 'center',
          scrollMode: 'if-needed',
        });
      }
    }, delay);
  }, [currentWidgetIndex, pageComponentId, layout, isDesktop, navigationView?.length]);

  const { colSpan, rowSpan, widgetHeight } = useMemo(() => {
    if (layout?.height && layout?.width) {
      return getLayoutSpan(layout);
    } else {
      return getLayoutSpan(DEFAULT_LAYOUT_GENERIC);
    }
  }, [layout]);

  const widgetKey = useMemo(() => {
    return `${pageComponentId}-${colSpan}-${rowSpan}-${isMobile ? 1 : 0}`;
  }, [colSpan, isMobile, pageComponentId, rowSpan]);

  return (
    <Box
      key={widgetKey}
      ref={ref}
      className={
        isDesktop
          ? cn(
              className,
              heightRestriction(widgetHeight),
              `col-span-${colSpan} row-span-${rowSpan}`,
            )
          : cn('col-span-12 row-span-1', className)
      }
      component="section"
      style={isDesktop || enforceHeightRestriction ? { height: widgetHeight } : {}}>
      <ErrorBoundary fallback={ErrorState}>{children}</ErrorBoundary>
    </Box>
  );
};

export default WidgetContainer;
